import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Plus, Search, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARISHES = [
  "Kingston", "St. Andrew", "St. Catherine", "Clarendon", "Manchester",
  "St. Elizabeth", "Westmoreland", "Hanover", "St. James", "Trelawny",
  "St. Ann", "St. Mary", "Portland", "St. Thomas"
];

const RELATIONSHIPS = ["Parent", "Spouse", "Child", "Sibling", "Guardian", "Other"];

const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    date_of_birth: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
    parish: '',
    nok_name: '',
    nok_address: '',
    nok_telephone: '',
    nok_relationship: '',
    chronic_illnesses: '',
    current_medications: '',
    allergies: ''
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchPatients();
    if (searchParams.get('action') === 'new') {
      setShowModal(true);
    }
  }, [searchParams]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/patients`);
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/patients`, formData);
      toast.success('Patient created successfully');
      setShowModal(false);
      setFormData({
        first_name: '', last_name: '', middle_name: '', date_of_birth: '', gender: '', phone: '', email: '',
        address: '', parish: '', nok_name: '', nok_address: '', nok_telephone: '', nok_relationship: '',
        chronic_illnesses: '', current_medications: '', allergies: ''
      });
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create patient');
    } finally {
      setSaving(false);
    }
  };

  const filteredPatients = patients.filter(p => {
    const search = searchTerm.toLowerCase();
    return (
      p.full_name?.toLowerCase().includes(search) ||
      p.first_name?.toLowerCase().includes(search) ||
      p.last_name?.toLowerCase().includes(search) ||
      p.middle_name?.toLowerCase().includes(search) ||
      p.email?.toLowerCase().includes(search) ||
      p.phone?.includes(searchTerm) ||
      p.parish?.toLowerCase().includes(search)
    );
  });

  return (
    <Layout>
      <div className="page-container" data-testid="patients-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Patients</h1>
            <p className="text-slate-500 mt-1">{patients.length} total patients</p>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-patient-btn"
          >
            <Plus className="w-4 h-4" />
            Add Patient
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Patient List */}
        <div className="card" data-testid="patients-list">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : filteredPatients.length > 0 ? (
            <div>
              {filteredPatients.map((patient) => (
                <div
                  key={patient.id}
                  className="patient-row"
                  onClick={() => navigate(`/patients/${patient.id}`)}
                  data-testid={`patient-item-${patient.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="patient-avatar">
                      {(patient.last_name || patient.first_name || patient.full_name || 'U').charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {patient.last_name && patient.first_name 
                          ? `${patient.last_name}, ${patient.first_name}${patient.middle_name ? ` ${patient.middle_name.charAt(0)}.` : ''}`
                          : patient.full_name
                        }
                      </p>
                      <div className="flex items-center gap-4 mt-1">
                        {patient.phone && (
                          <span className="flex items-center gap-1 text-sm text-slate-500">
                            <Phone className="w-3 h-3" /> {patient.phone}
                          </span>
                        )}
                        {patient.parish && (
                          <span className="flex items-center gap-1 text-sm text-slate-500">
                            <MapPin className="w-3 h-3" /> {patient.parish}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-500 capitalize">{patient.gender}</span>
                    <span className="text-sm text-slate-400">{patient.date_of_birth}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Users className="empty-state-icon" />
              <p className="empty-state-title">No patients found</p>
              <p className="empty-state-text">
                {searchTerm ? 'Try a different search term' : 'Add your first patient to get started'}
              </p>
            </div>
          )}
        </div>

        {/* Add Patient Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-patient-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">Add New Patient</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Details */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Personal Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="John"
                      required
                      data-testid="patient-first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="middle_name">Middle Name</Label>
                    <Input
                      id="middle_name"
                      value={formData.middle_name}
                      onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                      placeholder="Middle"
                      data-testid="patient-middle-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Doe"
                      required
                      data-testid="patient-last-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth *</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      required
                      data-testid="patient-dob-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender *</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                    >
                      <SelectTrigger data-testid="patient-gender-select">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 876 555 1234"
                      data-testid="patient-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="patient@email.com"
                      data-testid="patient-email-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Street address"
                      data-testid="patient-address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="parish">Parish</Label>
                    <Select
                      value={formData.parish}
                      onValueChange={(value) => setFormData({ ...formData, parish: value })}
                    >
                      <SelectTrigger data-testid="patient-parish-select">
                        <SelectValue placeholder="Select parish" />
                      </SelectTrigger>
                      <SelectContent>
                        {PARISHES.map((parish) => (
                          <SelectItem key={parish} value={parish}>{parish}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Clinical Summary */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Clinical Summary</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="chronic_illnesses">Chronic Illnesses</Label>
                    <Textarea
                      id="chronic_illnesses"
                      value={formData.chronic_illnesses}
                      onChange={(e) => setFormData({ ...formData, chronic_illnesses: e.target.value })}
                      placeholder="e.g., Diabetes, Hypertension, Asthma"
                      rows={2}
                      data-testid="patient-chronic-illnesses-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="current_medications">Current Medications</Label>
                    <Textarea
                      id="current_medications"
                      value={formData.current_medications}
                      onChange={(e) => setFormData({ ...formData, current_medications: e.target.value })}
                      placeholder="e.g., Metformin 500mg BD, Lisinopril 10mg OD"
                      rows={2}
                      data-testid="patient-medications-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="allergies">Allergies</Label>
                    <Textarea
                      id="allergies"
                      value={formData.allergies}
                      onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                      placeholder="e.g., Penicillin, Sulfa drugs, Peanuts"
                      rows={2}
                      data-testid="patient-allergies-input"
                    />
                  </div>
                </div>
              </div>

              {/* Next of Kin */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Next of Kin</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nok_name">Name</Label>
                    <Input
                      id="nok_name"
                      value={formData.nok_name}
                      onChange={(e) => setFormData({ ...formData, nok_name: e.target.value })}
                      placeholder="Next of kin name"
                      data-testid="patient-nok-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nok_relationship">Relationship</Label>
                    <Select
                      value={formData.nok_relationship}
                      onValueChange={(value) => setFormData({ ...formData, nok_relationship: value })}
                    >
                      <SelectTrigger data-testid="patient-nok-relationship-select">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATIONSHIPS.map((rel) => (
                          <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="nok_telephone">Telephone</Label>
                    <Input
                      id="nok_telephone"
                      type="tel"
                      value={formData.nok_telephone}
                      onChange={(e) => setFormData({ ...formData, nok_telephone: e.target.value })}
                      placeholder="+1 876 555 5678"
                      data-testid="patient-nok-telephone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nok_address">Address</Label>
                    <Input
                      id="nok_address"
                      value={formData.nok_address}
                      onChange={(e) => setFormData({ ...formData, nok_address: e.target.value })}
                      placeholder="Next of kin address"
                      data-testid="patient-nok-address-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-patient-btn">
                  {saving ? 'Saving...' : 'Save Patient'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Patients;
